import {
  LINK_TRUST_LOCK_NAME,
  LINK_TRUST_REVOKED_KEY,
  LINK_TRUST_REVOKED_TTL_MS
} from '~/constants/stripeLinkTrust.constants'
import { readLinkTrustRevocationsFromStorage } from '~/utils/readLinkTrustRevocationsFromStorage'
import { useStripeLinkTrustStore } from '~/stores/useStripeLinkTrustStore'
import { useLogger } from '~/composables/useLogger'
import type { LinkTrustRevocationsState } from '~/interfaces/LinkTrustRevocationsState'

const logger = useLogger('StripeLinkTrust')

const pruneExpired = (revocationsByIdentity: Record<string, number>): Record<string, number> => {
  const now = Date.now()
  const pruned: Record<string, number> = {}
  for (const [identityId, revokedAt] of Object.entries(revocationsByIdentity)) {
    if (now - revokedAt < LINK_TRUST_REVOKED_TTL_MS) pruned[identityId] = revokedAt
  }
  return pruned
}

const mergeRevocation = (
  departingIdentityId: number | null,
  current: LinkTrustRevocationsState
): LinkTrustRevocationsState => {
  const revocationsByIdentity = pruneExpired(current.revocationsByIdentity)
  if (departingIdentityId === null) {
    return { revocationsByIdentity, universalRevokedAt: Date.now() }
  }
  revocationsByIdentity[String(departingIdentityId)] = Date.now()
  return { revocationsByIdentity, universalRevokedAt: current.universalRevokedAt }
}

const persist = (merged: LinkTrustRevocationsState): void => {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(LINK_TRUST_REVOKED_KEY, JSON.stringify(merged))
  } catch (error: unknown) {
    // Unpersisted, so a reload sees the old value -- the in-memory state above still fails closed now.
    logger.error('persistFailed', {
      errorName: error instanceof Error ? error.name : 'UnknownError'
    })
  }
}

// null revokes browser-wide with no per-identity exception (401, stale cleanup, or a save).
export const revokeStripeLinkTrust = async (departingIdentityId: number | null): Promise<void> => {
  const store = useStripeLinkTrustStore()

  const applyRevocation = (): void => {
    // Re-reading on-disk truth (instead of this tab's in-memory copy) closes the cross-tab lost-update race.
    const current =
      typeof window === 'undefined'
        ? store
        : readLinkTrustRevocationsFromStorage(() => localStorage, LINK_TRUST_REVOKED_KEY)
    const merged = mergeRevocation(departingIdentityId, current)
    store.revocationsByIdentity = merged.revocationsByIdentity
    store.universalRevokedAt = merged.universalRevokedAt
    persist(merged)
  }

  if (typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function') {
    await navigator.locks.request(LINK_TRUST_LOCK_NAME, () => {
      applyRevocation()
    })
    return
  }
  applyRevocation()
}
